import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../store';
import { canAccessAdminUi } from '../adminAccess';

/** Событие: принудительно сравнить бандл с `/version.json` (например из профиля админа). */
export const ONVIZ_PROBE_WEBAPP_VERSION = 'onviz-probe-webapp-version';

type RemoteVersion = { commit?: string; deploy?: string; builtAt?: string };

function normCommit(s: string): string {
  return String(s ?? '').trim().toLowerCase();
}

/** Перезагрузка WebView: при возможности уведомляем Telegram, затем обычный reload. */
export function reloadWebappForNewDeploy(): void {
  const tw = (window as Window & { Telegram?: { WebApp?: { postEvent?: (name: string) => void } } }).Telegram?.WebApp;
  try {
    tw?.postEvent?.('iframe_will_reload');
  } catch {
    /* ignore */
  }
  window.location.reload();
}

/**
 * Жёсткий сброс кэша WebView: новый URL с query (Telegram часто кэширует document по path без query).
 * Подтягивает свежий `index.html` с Vercel, если на origin уже новый деплой. Если на Vercel всё ещё старый билд — SHA не изменится.
 */
export async function forceReloadWebappBypassCache(): Promise<void> {
  const tw = (window as Window & { Telegram?: { WebApp?: { postEvent?: (name: string) => void } } }).Telegram?.WebApp;
  try {
    tw?.postEvent?.('iframe_will_reload');
  } catch {
    /* ignore */
  }
  let remoteCommit = '';
  try {
    const r = await fetch(`${window.location.origin}/version.json?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (r.ok) {
      const j = (await r.json()) as RemoteVersion;
      remoteCommit = String(j?.commit ?? '').trim();
    }
  } catch {
    /* ignore */
  }
  const u = new URL(window.location.href);
  u.searchParams.delete('_onviz_cb');
  u.searchParams.delete('_onviz_v');
  u.searchParams.set('_onviz_cb', String(Date.now()));
  if (remoteCommit) u.searchParams.set('_onviz_v', remoteCommit);
  window.location.replace(u.toString());
}

/**
 * Сравнивает вшитые в бандл __APP_COMMIT__ / __APP_DEPLOY_HINT__ с живым `/version.json` на origin.
 * После нового деплоя Vercel файл на сервере новее уже загруженного JS → показываем плашку «Обновить».
 */
export function useWebappUpdateProbe(): { updateAvailable: boolean; reload: () => void; probe: () => Promise<void> } {
  const role = useAppStore((s) => s.role);
  const telegramId = useAppStore((s) => s.telegramId);
  const authAdminHint = useAppStore((s) => s.authAdminHint);
  const showAdminChrome = useMemo(
    () => canAccessAdminUi({ role, telegramId, serverAdminPanelHint: authAdminHint }),
    [role, telegramId, authAdminHint],
  );
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const intervalMs = showAdminChrome ? 18_000 : 55_000;

  const probe = useCallback(async () => {
    try {
      const url = `${window.location.origin}/version.json?ts=${Date.now()}`;
      const r = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const j = (await r.json()) as RemoteVersion;
      const remoteC = normCommit(j.commit ?? '');
      const remoteD = String(j.deploy ?? '').trim();
      const localC = normCommit(__APP_COMMIT__);
      const localD = String(__APP_DEPLOY_HINT__ ?? '').trim();

      if (!remoteC) return;

      const commitDiff = remoteC !== localC;
      const sameCommitRedeploy = remoteC === localC && Boolean(remoteD && localD && remoteD !== localD);

      setUpdateAvailable(commitDiff || sameCommitRedeploy);
    } catch {
      /* офлайн / dev без файла */
    }
  }, []);

  useEffect(() => {
    const t0 = window.setTimeout(() => void probe(), 2500);
    const id = window.setInterval(() => void probe(), intervalMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') void probe();
    };
    const onManual = () => void probe();
    window.addEventListener('visibilitychange', onVis);
    window.addEventListener(ONVIZ_PROBE_WEBAPP_VERSION, onManual);
    return () => {
      window.clearTimeout(t0);
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(ONVIZ_PROBE_WEBAPP_VERSION, onManual);
    };
  }, [probe, intervalMs]);

  const reload = useCallback(() => {
    reloadWebappForNewDeploy();
  }, []);

  return { updateAvailable, reload, probe };
}
